import { useMutation, useQueries, useQuery } from "@tanstack/react-query"
import ModelNumbersService from "services/modelNumbers.service"
import type { TDimensions, TModelNumberDetails, TModelNumberPricePayloadItem } from "types/modelNumbers.type"
import type { AxiosResponse } from "axios"

type TAddModelNumberPayload = {
	formData: FormData
}

export const useGetModelNumbers = () => {
	return useQuery({
		queryKey: ["getModelNumbers"],
		queryFn: () => ModelNumbersService.getModelNumbers(),
		select: (response) => response.data,
	})
}

export const useGetModelNumber = (id: number, enabled = true) => {
	return useQuery({
		queryKey: ["getModelNumber", id],
		queryFn: () => ModelNumbersService.getModelNumber(id),
		select: (response) => response.data,
		enabled: enabled && !!id,
	})
}

export const useGetModelNumberDetailsByIds = (ids: number[], enabled = true) => {
	return useQueries({
		queries: ids.map((id) => ({
			queryKey: ["getModelNumber", id],
			queryFn: () => ModelNumbersService.getModelNumber(id) as Promise<AxiosResponse<TModelNumberDetails>>,
			select: (response: AxiosResponse<TModelNumberDetails>) => response.data,
			enabled: enabled && !!id,
		})),
	})
}

export const useGetModelNumberImageLink = (id: number) => {
	return useQuery({
		queryKey: ["getModelNumberImageLink", id],
		queryFn: () => ModelNumbersService.getModelNumberImageLink(id),
		select: (response) => response.data,
		enabled: !!id,
	})
}

export const useGetModelNumberImageLinksByIds = (ids: number[], enabled = true) => {
	return useQueries({
		queries: ids.map((id) => ({
			queryKey: ["getModelNumberImageLink", id],
			queryFn: () => ModelNumbersService.getModelNumberImageLink(id),
			select: (response: AxiosResponse<{ download_url: string }>) => response.data,
			enabled: enabled && !!id,
			retry: false,
		})),
	})
}

export const useAddModelNumber = () => {
	return useMutation({
		mutationFn: ({ formData }: TAddModelNumberPayload) => ModelNumbersService.addModelNumber(formData),
	})
}

export const useEditModelNumber = () => {
	return useMutation({
		mutationFn: ({ modelNumber, id }: { modelNumber: string; id: number }) =>
			ModelNumbersService.editModelNumber(modelNumber, id),
	})
}

export const useDeleteModelNumber = () => {
	return useMutation({
		mutationFn: (id: number) => ModelNumbersService.deleteModelNumber(id),
	})
}

export const useUploadModelNumberImage = () => {
	return useMutation({
		mutationFn: ({ id, image }: { id: number; image: File }) => ModelNumbersService.uploadModelNumberImage(id, image),
	})
}

export const useUpdateModelNumberCategories = () => {
	return useMutation({
		mutationFn: ({ id, relatedCategoryId }: { id: number; relatedCategoryId: number | null }) =>
			ModelNumbersService.updateModelNumberCategories(id, relatedCategoryId),
	})
}

export const useUpdateModelNumberDimensions = () => {
	return useMutation({
		mutationFn: ({ id, dimensions }: { id: number; dimensions: TDimensions }) =>
			ModelNumbersService.updateModelNumberDimensions(id, dimensions),
	})
}

export const useUpdateModelNumberPrices = () => {
	return useMutation({
		mutationFn: ({ id, prices }: { id: number; prices: TModelNumberPricePayloadItem[] }) =>
			ModelNumbersService.updateModelNumberPrices(id, prices),
	})
}
